<template>
  <div v-if="solution">
    <Breadcrumb :items="crumbs" />

    <section class="container hero">
      <div class="badges">
        <span class="chip" :style="{ background: solution.chip }" aria-hidden="true"></span>
        <span v-if="pick(solution, 'audience')" class="tag">{{ ui('built_for') }}: {{ pick(solution, 'audience') }}</span>
      </div>
      <h1 class="h-title">{{ pick(solution, 'name') }}</h1>
      <p class="lead">{{ pick(solution, 'description') }}</p>
      <div class="buttons">
        <PillButton :to="`/contact?solution=${solution.slug}`">{{ ui('discuss_solution') }}</PillButton>
        <PillButton to="/products" variant="secondary">{{ t('cta2') }}</PillButton>
      </div>
    </section>

    <section v-if="paragraphs.length || features.length" class="section container detail">
      <div v-if="paragraphs.length" class="about">
        <h2 class="h2 heading">{{ ui('about_solution') }}</h2>
        <p v-for="(paragraph, index) in paragraphs" :key="index" class="body">{{ paragraph }}</p>
      </div>
      <div v-if="features.length" class="included">
        <h2 class="h2 heading">{{ ui('whats_included') }}</h2>
        <ul class="features">
          <li v-for="(feature, index) in features" :key="index">
            <span class="dot" :style="{ background: solution.chip }" aria-hidden="true"></span>
            {{ feature }}
          </li>
        </ul>
      </div>
    </section>

    <section v-if="practice.length" class="section container">
      <h2 class="h2 heading related-title">{{ labels.see_in_practice }}</h2>
      <ul class="practice">
        <li v-for="study in practice" :key="study.slug">
          <router-link :to="to(`/case-studies/${study.slug}`)">{{ study.title }} {{ arrow }}</router-link>
          <span>{{ study.clientLabel }}</span>
        </li>
      </ul>
    </section>

    <section class="section container">
      <h2 class="h2 heading related-title">{{ ui('related_solutions') }}</h2>
      <div class="grid">
        <SolutionCard v-for="item in related" :key="item.slug" :solution="item" :min-height="260" :title-size="28" />
      </div>
    </section>

    <CtaBand />
  </div>
</template>

<script setup>
import { computed, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Breadcrumb from '../components/Breadcrumb.vue'
import PillButton from '../components/PillButton.vue'
import SolutionCard from '../components/SolutionCard.vue'
import CtaBand from '../components/CtaBand.vue'
import { useLocale } from '../composables/useLocale'
import { usePageMeta } from '../composables/usePageMeta'
import { useCaseStudies } from '../composables/useCaseStudies'
import siteFeatures from '../config/features.json'
import { solutions } from '../data'

const route = useRoute()
const router = useRouter()
const { t, ui, pick, to, arrow } = useLocale()
const { studies, labels } = useCaseStudies()

const solution = computed(() => solutions.find((item) => item.slug === route.params.slug))

// Old product links (and typos) land on the Solutions page rather than a dead end.
watchEffect(() => {
  if (!solution.value) {
    router.replace(to('/products'))
  }
})

const crumbs = computed(() => [
  { label: ui('home'), to: '/' },
  { label: ui('solutions'), to: '/products' },
  { label: pick(solution.value, 'name') }
])

const paragraphs = computed(() => (pick(solution.value, 'details') || '')
  .split(/\n{2,}/)
  .map((paragraph) => paragraph.trim())
  .filter(Boolean))

const features = computed(() => pick(solution.value, 'features') || [])

// Published case studies that point at this solution. Hidden along with the
// rest of the section while showCaseStudies is false in src/config/features.json.
const practice = computed(() => (siteFeatures.showCaseStudies
  ? studies.value.filter((study) => study.relatedProductSlugs.includes(solution.value?.slug))
  : []))

// The next three solutions in the list, wrapping around.
const related = computed(() => {
  const index = solutions.findIndex((item) => item.slug === solution.value?.slug)
  return [1, 2, 3].map((offset) => solutions[(index + offset) % solutions.length])
})

usePageMeta(
  computed(() => solution.value && (solution.value.seoTitle || `${pick(solution.value, 'name')} | ${t('brand')}`)),
  computed(() => solution.value && (solution.value.seoDescription || pick(solution.value, 'description')))
)
</script>

<style scoped>
.hero {
  padding-top: clamp(32px, 6vw, 64px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
}

.hero .lead {
  max-width: 52ch;
}

.badges {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.chip {
  width: 56px;
  height: 56px;
  border-radius: 16px;
}

.badges .tag {
  background: var(--surface);
  font-size: 14px;
  padding: 8px 14px;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

.detail {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 440px), 1fr));
  gap: clamp(32px, 5vw, 72px);
  align-items: start;
}

.heading {
  font-size: clamp(32px, 4vw, 48px);
  margin-bottom: 24px;
}

.body {
  font-size: 18px;
  line-height: 1.7;
  color: var(--muted);
  max-width: 62ch;
}

.body + .body {
  margin-top: 1em;
}

.features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.features li {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--surface);
  border-radius: var(--radius-inner);
  padding: 18px 22px;
  font-size: 17px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  flex-shrink: 0;
}

.related-title {
  margin-bottom: 32px;
}

.practice {
  list-style: none;
  margin: -8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.practice li {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--surface);
  border-radius: var(--radius-inner);
  padding: 18px 22px;
}

.practice a {
  font-size: 19px;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.practice span {
  font-size: 15px;
  color: var(--tertiary);
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 16px;
}
</style>
